import { PaginationQueryParams } from '@weenat/client/dist/resources/types'
import { useIntl } from '@weenat/wintl'
import { FilterCategory, ListFilter } from 'app/utils/lists'
import { isEmpty, isNil } from 'lodash-es'
import { useMemo, useState } from 'react'
import { css, styled, useTheme } from 'styled-components'
import useDisclosure from '../hooks/useDisclosure'
import Badge from './Badge'
import Button from './Button'
import Checkbox from './Checkbox'
import DelimitedFlex from './DelimitedFlex'
import InvisibleTextInput from './fields/InvisibleInput'
import RadioIndicator from './fields/RadioIndicator'
import { Dropdown, DropdownContent, DropdownTrigger } from './fields/Select/SelectDropdown'
import Icons from './Icons'
import MenuItem from './MenuItem'
import { FlexProps } from './primitives/themeMappings/props'
import Scrollable from './Scrollable'
import Text from './Text'
import WeenatPressable from './WeenatPressable'

const badgeSectionStyle = css`
  display: flex;
  flex-direction: row;
  padding: 8px;
  border-left: 1px solid ${(p) => p.theme.colors.grayscale[300]};
`

const BadgeSection = styled.div`
  ${badgeSectionStyle}
`

const PressableBadgeSection = styled(WeenatPressable)`
  ${badgeSectionStyle}
`

const BadgeText = styled(Text)`
  user-select: none;
`

const MenuItemText = styled(BadgeText)`
  flex: 1;
`

interface FilterMenuItemProps {
  onPress: () => void
  label: string
  showCheckbox?: boolean
  showChevron?: boolean
  showRadio?: boolean
  checked?: boolean
}

const FilterMenuItem = ({
  onPress,
  label,
  showCheckbox = false,
  showChevron = false,
  showRadio = false,
  checked = false
}: FilterMenuItemProps) => {
  return (
    <MenuItem onPress={onPress}>
      <Flex $alignItems='center' $p='md' $gap='md'>
        {showCheckbox ? <Checkbox type='checkbox' checked={checked} /> : null}
        {showRadio ? <RadioIndicator isChecked={checked} /> : null}
        <MenuItemText>{label}</MenuItemText>
        {showChevron ? <Icons.ChevronRight /> : null}
      </Flex>
    </MenuItem>
  )
}

interface ActiveFilterCategoryProps<
  CategoryID extends string = string,
  QueryParams = PaginationQueryParams
> {
  category: FilterCategory<CategoryID, QueryParams>
  onFilterSelection: (filter: ListFilter) => void
  onRemove: () => void
}

function ActiveFilterCategory<
  CategoryID extends string = string,
  QueryParams = PaginationQueryParams
>({ category, onFilterSelection, onRemove }: ActiveFilterCategoryProps<CategoryID, QueryParams>) {
  const { isOpen, toggle, open, close } = useDisclosure(false)

  const activeFilters = category.filters.filter((cat) => cat.active)

  const { t } = useIntl()
  const [searchQuery, setSearchQuery] = useState<string>('')

  const filteredResults = useMemo(
    () =>
      category.filters.filter(
        (option) => isEmpty(searchQuery) || option.label.includes(searchQuery.toLowerCase())
      ),
    [category.filters, searchQuery]
  )

  return (
    <Dropdown
      isVisible={isOpen}
      onVisibleChange={(visible) => {
        if (visible) {
          open()
        } else {
          setSearchQuery('')
          close()
        }
      }}
      placement='bottom-start'
      followTriggerElement={false}
    >
      <DropdownTrigger>
        <Badge
          onClick={toggle}
          $borderRadius={'md'}
          $backgroundColor={`grayscale.100`}
          $borderColor={`grayscale.300`}
          $borderWidth={'sm'}
          $p={0}
          $px={0}
          $py={0}
          $cursor='pointer'
          $overflow='hidden'
        >
          <Box $p='md'>
            <BadgeText $fontWeight='bold'>{category.label}</BadgeText>
          </Box>
          {activeFilters.map((activeFilter) => (
            <BadgeSection key={activeFilter.id}>
              <BadgeText>{activeFilter.label}</BadgeText>
            </BadgeSection>
          ))}
          <PressableBadgeSection onPress={onRemove}>
            <Icons.Close $p={0} />
          </PressableBadgeSection>
        </Badge>
      </DropdownTrigger>
      <DropdownContent width={300}>
        <Box $pb={'sm'} $pt={category.isSearchable ? 0 : 'sm'}>
          {category?.isSearchable && (
            <Flex $backgroundColor={'grayscale.100'} $p='md' $alignItems='center' $mb='sm'>
              <Icons.Search $size='md' />
              <InvisibleTextInput
                shouldFocusOnMount
                autoComplete='on'
                autoCorrect='off'
                type='text'
                value={searchQuery}
                onChange={(e) => {
                  if (!isNil(e.currentTarget.value)) setSearchQuery(e.currentTarget.value)
                }}
                placeholder={t('actions.search')}
              />
            </Flex>
          )}
          {!isEmpty(searchQuery) && isEmpty(filteredResults) ? (
            <Box $py='lg' $px='lg'>
              <Text $textAlign='center'>
                {t('map.no_summaries_matching_search_term', {
                  searchTerm: searchQuery
                })}
              </Text>
            </Box>
          ) : (
            filteredResults.map((opt) => {
              return (
                <FilterMenuItem
                  key={opt.id}
                  onPress={() => {
                    onFilterSelection(opt)

                    if (!category.multi) {
                      toggle()
                    }
                  }}
                  label={opt.label}
                  showCheckbox={category.multi}
                  showRadio={!category.multi}
                  checked={opt.active}
                />
              )
            })
          )}
        </Box>
      </DropdownContent>
    </Dropdown>
  )
}

interface FilterButtonProps<
  CategoryID extends string = string,
  QueryParams = PaginationQueryParams
> {
  categories: FilterCategory<CategoryID, QueryParams>[]
  onFilterSelection: (category: FilterCategory<CategoryID, QueryParams>, filter: ListFilter) => void
}

function FilterButton<CategoryID extends string = string, QueryParams = PaginationQueryParams>({
  categories,
  onFilterSelection
}: FilterButtonProps<CategoryID, QueryParams>) {
  const { t } = useIntl()
  const { colors } = useTheme()

  const { isOpen, toggle, open, close } = useDisclosure(false)

  const [openedCategoryId, setOpenedCategoryId] = useState<string | undefined>(undefined)

  const openedCategory = useMemo<FilterCategory<CategoryID, QueryParams> | undefined>(() => {
    if (isNil(openedCategoryId)) return undefined
    else return categories.find((cat) => cat.id === openedCategoryId)
  }, [categories, openedCategoryId])

  const [searchQuery, setSearchQuery] = useState<string>('')

  const filteredResults = useMemo(() => {
    if (isNil(openedCategory)) return []
    return openedCategory.filters.filter(
      (option) =>
        isEmpty(searchQuery) || option.label.toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [categories, openedCategory, searchQuery])

  return (
    <Dropdown
      isVisible={isOpen}
      onVisibleChange={(visible) => {
        if (visible) {
          open()
        } else {
          close()
        }
        setOpenedCategoryId(undefined)
      }}
      placement='bottom-start'
      followTriggerElement={false}
    >
      <DropdownTrigger>
        <Button
          onPress={toggle}
          IconLeft={Icons.FilterList}
          backgroundColor={'grayscale.100'}
          color={'grayscale.black'}
          style={{
            padding: 8,
            minHeight: 'unset',
            borderRadius: 8
          }}
        >
          {t('filtering.filters', { capitalize: true })}
        </Button>
      </DropdownTrigger>
      <DropdownContent
        width={300}
        Header={
          !isNil(openedCategory) ? (
            <>
              <DelimitedFlex
                $backgroundColor={colors.grayscale.white}
                $alignItems='center'
                $p='md'
                $gap='lg'
              >
                <Icons.ArrowBack
                  $p='sm'
                  onPress={() => {
                    setSearchQuery('')
                    setOpenedCategoryId(undefined)
                  }}
                />
                <BadgeText $fontSize='sm' $fontWeight='medium'>
                  {openedCategory.label}
                </BadgeText>
              </DelimitedFlex>
              {openedCategory?.isSearchable && (
                <Flex $backgroundColor={'grayscale.100'} $p='md' $alignItems='center'>
                  <Icons.Search $size='md' />
                  <InvisibleTextInput
                    shouldFocusOnMount
                    autoComplete='on'
                    autoCorrect='off'
                    type='text'
                    value={searchQuery}
                    onChange={(e) => {
                      if (!isNil(e.target)) setSearchQuery(e.target.value)
                    }}
                    placeholder={t('actions.search')}
                  />
                </Flex>
              )}
            </>
          ) : null
        }
      >
        <Box $py={'sm'}>
          {openedCategory === undefined ? (
            <>
              {categories.map((c) => {
                return (
                  <FilterMenuItem
                    key={c.id}
                    onPress={() => setOpenedCategoryId(c.id)}
                    label={c.label}
                    showChevron
                  />
                )
              })}
            </>
          ) : (
            <Scrollable $maxHeight={400} $isDiscrete>
              {!isEmpty(searchQuery) && isEmpty(filteredResults) ? (
                <Box $py='lg' $px='lg'>
                  <Text $textAlign='center'>
                    {t('map.no_summaries_matching_search_term', {
                      searchTerm: searchQuery
                    })}
                  </Text>
                </Box>
              ) : (
                openedCategory.filters.map((opt) => {
                  return (
                    <FilterMenuItem
                      key={opt.id}
                      onPress={() => {
                        onFilterSelection(openedCategory, opt)

                        if (!openedCategory.multi) {
                          setOpenedCategoryId(undefined)
                          toggle()
                        }
                      }}
                      checked={opt.active}
                      showCheckbox={openedCategory.multi}
                      showRadio={!openedCategory.multi}
                      label={opt.label}
                    />
                  )
                })
              )}
            </Scrollable>
          )}
        </Box>
      </DropdownContent>
    </Dropdown>
  )
}

interface FiltersBarProps<CategoryID extends string = string, QueryParams = PaginationQueryParams> {
  categories: FilterCategory<CategoryID, QueryParams>[]
  onFilterSelection: (category: FilterCategory<CategoryID, QueryParams>, filter: ListFilter) => void
  onFilterCategoryReset: (category: FilterCategory<CategoryID, QueryParams>) => void
  $mx?: FlexProps['$mx']
}

function FiltersBar<CategoryID extends string = string, QueryParams = PaginationQueryParams>({
  categories,
  onFilterSelection,
  onFilterCategoryReset,
  $mx = 0
}: FiltersBarProps<CategoryID, QueryParams>) {
  const activeCategories = categories.filter((cat) => cat.filters.some((f) => f.active))

  return (
    <Flex $alignItems='center' $flexWrap='wrap' $gap='md' $mx={$mx}>
      {activeCategories.map((cat) => (
        <ActiveFilterCategory
          key={cat.id}
          category={cat}
          onFilterSelection={(filter) => onFilterSelection(cat, filter)}
          onRemove={() => onFilterCategoryReset(cat)}
        />
      ))}
      <FilterButton categories={categories} onFilterSelection={onFilterSelection} />
    </Flex>
  )
}

export default FiltersBar
